import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../utils/api-client';

export interface Balance {
  effectiveBalanceFormatted: string;
  effectiveBalance: number;
  displayCurrency: string;
  currencySymbol: string;
  usdRate: number;
}

interface BalanceContextType {
  balance: Balance | null;
  refreshBalance: () => Promise<void>;
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};

export const BalanceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<Balance | null>(null);

  const refreshBalance = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiClient.get<Balance>('/accounts/user/balance');
      setBalance(data);
    } catch {
      // Silent — balance display is best-effort
    }
  }, [user]);

  // Listen for balance-changed events from anywhere in the app
  useEffect(() => {
    const handler = () => { refreshBalance(); };
    window.addEventListener('balance-changed', handler);
    return () => window.removeEventListener('balance-changed', handler);
  }, [refreshBalance]);

  const value = useMemo(() => ({ balance, refreshBalance }), [balance, refreshBalance]);

  return (
    <BalanceContext.Provider value={value}>
      {children}
    </BalanceContext.Provider>
  );
};
